import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { selectUser, selectUserTags } from '@/store/selectors/userSelectors';
import {
  GIG_REGULATED_OPTIMOVE_TENANT_ID,
  GIG_UNREGULATED_OPTIMOVE_TENANT_ID,
} from '@/constants/constants';
import { isRegulatedMarket } from '@/utils/utils';

function getWindow(): any {
  return window;
}

export const openZendesk = (): void => {
  const w = getWindow();
  if (w && w.zE) {
    w.zE('messenger', 'show');
    w.zE('messenger', 'open');
  }
};

export const getZendeskLocale = (locale: string): string => {
  switch (locale) {
    case 'ca-fr':
      return 'fr';
    case 'th':
      return 'th';
    case 'mx':
      return 'es';
    case 'ca':
    case 'row':
    case 'nz':
    case 'za':
    case 'ae':
      return 'en';
    default:
      return locale;
  }
};

export function Zendesk({ locale }: { locale: string }): JSX.Element | null {
  const [zendeskScriptLoaded, setZendeskScriptLoaded] = useState(false);
  const [zendeskShown, setZendeskShown] = useState(false);
  const user = useSelector(selectUser);
  const tags = useSelector(selectUserTags);

  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    if (window && document && !zendeskScriptLoaded) {
      script = document.createElement('script');
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${process.env.REACT_APP_ZENDESK_KEY}`;
      script.id = 'ze-snippet';
      script.async = true;
      script.onload = () => {
        setZendeskScriptLoaded(true);
      };
      document.head.appendChild(script);
    }

    return () => {
      if (script) {
        script.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!zendeskScriptLoaded) {
      return;
    }
    const w = getWindow();
    if (w && w.zE) {
      w.zE('messenger', 'hide');

      w.zE('messenger:set', 'locale', getZendeskLocale(locale));
      w.zE('messenger:on', 'open', function () {
        setZendeskShown(true);
      });

      w.zE('messenger:on', 'close', function () {
        setZendeskShown(false);
      });
    }
  }, [zendeskScriptLoaded, locale]);

  useEffect(() => {
    if (!zendeskScriptLoaded) {
      return;
    }
    const w = getWindow();
    if (w && w.zE) {
      w.zE('messenger:set', 'conversationFields', [
        { id: '25891516613778', value: window.location.href },
        { id: '33763611159442', value: user?.userId },
        { id: '33763688678802', value: user?.email },
        { id: '33763691973266', value: (tags ?? []).map(tag => tag.name).join(';') },
        {
          id: '33763647807634',
          value: isRegulatedMarket(locale)
            ? GIG_REGULATED_OPTIMOVE_TENANT_ID
            : GIG_UNREGULATED_OPTIMOVE_TENANT_ID,
        },
      ]);
    }
  }, [zendeskScriptLoaded, user, tags, locale]);

  return (
    <>
      <Helmet>
        {!zendeskShown && (
          <style type="text/css">
            {`iframe#launcher {
          display: none;
        }`}
          </style>
        )}
      </Helmet>
    </>
  );
}
